// F123 (Pavel 2026-08-11): a rail step that CONTAINS steps says how far
// through them the client is.
//
// "Tam, kde je vic kroku... kdyby na te ikonce toho kolecka bylo nejakym
// zpusobem videt, ale vizualne velmi vkusnym zpusobem, to je klicove, a
// decentnim, nenapadnym, ze mam treba jeden krok ze tri hotovo... mozna ten
// ramecek toho kolecka by byl zeleny po tretinach."
//
// 🔴 ONE derivation, on the SERVER. The rail marker, the beta step's own
// substep marks and every guard read the map this module builds from raw
// facts; no surface may count substeps again from its own DTOs, or the ring
// and the page it describes drift apart on the same screen.
//
// Two shapes, because the two families of steps are different objects:
//   * `count` - a small, NAMED set of substeps (beta 3, sales page 3,
//     handover 3). The fraction is a fact the client can point at, so the
//     count may be shown.
//   * `smooth` - a questionnaire. Pavel: "ten zeleny okraj by se uzaviral o
//     kousicek s kazdou vyplnenou otazkou, bez zlomku." Forty-five answers
//     are not a countable ladder; the ring alone carries it.

/** Rail steps that CONTAIN steps. Everything else has no progress to report. */
export const SUBSTEP_RAIL_KEYS = ["q1", "q2", "beta", "salespage", "handover"] as const;
export type SubstepRailKey = (typeof SUBSTEP_RAIL_KEYS)[number];

export type RailProgressMode = "count" | "smooth";

export interface RailStepProgress {
  /** Substeps closed. Never above `total`, never below 0. */
  done: number;
  total: number;
  mode: RailProgressMode;
  /**
   * The closed substeps by name, in journey order (`count` steps only). The
   * beta page reads these instead of re-deriving its own ticks.
   */
  marks: string[];
}

export type RailStepProgressMap = Partial<Record<SubstepRailKey, RailStepProgress>>;

/**
 * The named substeps of each `count` step, in the order the client walks
 * them. The names are the CLIENT's steps on that page, so the page and the
 * ring can never disagree about how many there are.
 */
// 🔴 F133 (Pavel 2026-08-11): the handover lost its folder substep, so the
// count went 3 -> 2. The client's Předání now shows exactly two steps, and the
// ring is a share of what the page shows: leaving "folder" here would paint
// thirds over a two-step ladder and report 1/3 to somebody who had finished
// half. The same edit lands in `worker/src/rail-substeps.ts`, which stopped
// reading the folder fact - one derivation, both halves or neither.
export const RAIL_SUBSTEP_NAMES = {
  beta: ["chat", "transfer", "testers"],
  salespage: ["approve", "links", "publish"],
  handover: ["ownership", "dashboard"]
} as const satisfies Record<"beta" | "salespage" | "handover", readonly string[]>;

export interface RailSubstepFacts {
  /** Answered / total for each questionnaire, or null when nothing is known. */
  q1: { answered: number; total: number } | null;
  q2: { answered: number; total: number } | null;
  beta: {
    /** F124: the client opened the trial chat, or already owns the bot. */
    chatOpened: boolean;
    /** The verified bot ownership transfer - never a self-declared done. */
    transferred: boolean;
    /** F124: the client copied the tester link at least once. */
    testersInvited: boolean;
  };
  salespage: {
    approved: boolean;
    /** F82: the order links reached the page (or the historic Stripe gate). */
    linksIntegrated: boolean;
    published: boolean;
  };
  handover: {
    /** F133 step 1: the stored Telegram ID. */
    ownershipStored: boolean;
    /** F133 step 2: the client confirmed the Owner Dashboard. */
    dashboardConfirmed: boolean;
  };
}

function countProgress(
  key: keyof typeof RAIL_SUBSTEP_NAMES,
  closed: readonly boolean[]
): RailStepProgress {
  const names = RAIL_SUBSTEP_NAMES[key];
  const marks = names.filter((_, index) => closed[index] === true);
  return { done: marks.length, total: names.length, mode: "count", marks: [...marks] };
}

function smoothProgress(
  counts: { answered: number; total: number } | null
): RailStepProgress | null {
  if (!counts || counts.total <= 0) return null;
  // A stored answer for a question the catalog no longer shows must not push
  // the ring past full: the arc is a share of the journey, not a tally.
  const done = Math.max(0, Math.min(counts.answered, counts.total));
  return { done, total: counts.total, mode: "smooth", marks: [] };
}

/** The ONE map. Facts in, progress out; no I/O, no dates, no locale. */
export function deriveRailStepProgress(facts: RailSubstepFacts): RailStepProgressMap {
  const map: RailStepProgressMap = {
    beta: countProgress("beta", [
      facts.beta.chatOpened,
      facts.beta.transferred,
      facts.beta.testersInvited
    ]),
    salespage: countProgress("salespage", [
      facts.salespage.approved,
      facts.salespage.linksIntegrated,
      facts.salespage.published
    ]),
    handover: countProgress("handover", [
      facts.handover.ownershipStored,
      facts.handover.dashboardConfirmed
    ])
  };
  const q1 = smoothProgress(facts.q1);
  if (q1) map.q1 = q1;
  const q2 = smoothProgress(facts.q2);
  if (q2) map.q2 = q2;
  return map;
}

/** True when this step's page really has named substeps behind it. */
export function railSubstepMarked(
  progress: RailStepProgress | null | undefined,
  mark: string
): boolean {
  return progress?.marks.includes(mark) === true;
}

/**
 * The share of the ring to paint, 0..1. A step the client has not opened yet
 * reports nothing: an arc on a padlock would promise progress inside a step
 * that has not begun.
 */
export function railProgressFraction(
  progress: RailStepProgress | null | undefined
): number {
  if (!progress || progress.total <= 0 || progress.done <= 0) return 0;
  return Math.min(1, progress.done / progress.total);
}
